import { observer } from "mobx-react"
import { createRef, useEffect, useMemo } from "react"
import useUpdateEffect from "../../hooks/useUpdateEffect"
import { toJS } from "mobx"
import { useContainerInjection } from "../../hooks/useContainerInjection"
import { UsersViewModel } from "./UsersViewModel"
import { User } from "../../../domain/entities/User"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import MainForm, { MainFormRef } from "../../components/Form/MainForm"
import { Col, Row } from "antd"
import { Card } from "../../styledComponents/CustomCard/Card"
import UserInformationFormSection from "./components/UserInformationFormSection"
import UserDetailFormSkeleton from "../../styledComponents/LoadingSkeletons/MicrositeDetailFormSkeleton copy"
import { Language } from "../../../locale/interfaces/Translation"

const UsersFormView = () => {
	const { t } = useTranslation("users", { keyPrefix: "form" })
	const location = useLocation()
	const locationState = location.state as { user?: User } | undefined
	const { id } = useParams<{ id: string }>()
	const formRef = createRef<MainFormRef>()
	const viewModel = useContainerInjection<UsersViewModel>("UsersViewModel")
	const navigate = useNavigate()

	useEffect(() => {
		if (locationState?.user) {
			viewModel.setEditMode(locationState.user.id !== undefined)
			viewModel.setInitialFormData(locationState.user)
			viewModel.setFormData(locationState.user)
			viewModel.setOriginalFormData(locationState.user)
			return
		}

		if (id) {
			viewModel
				.fetchUserDetail(id)
				.then(user => {
					viewModel.setEditMode(true)
					viewModel.setInitialFormData(user)
					viewModel.setFormData(user)
					viewModel.setOriginalFormData(user)
				})
				.catch(() => {
					navigate("/users", { replace: true })
				})
			return
		}

		const emptyUser: Partial<User> = {
			name: "",
			userName: "",
			city: "",
			email: "",
			bio: "",
			language: Language.esES,
			goal: "",
			phoneNumber: "",
			password: "",
			birthdate: undefined,
			role: User.roles[0].value,
			status: User.status[0].value
		}

		viewModel.setEditMode(false)
		viewModel.setInitialFormData(emptyUser)
		viewModel.setFormData(emptyUser)
		viewModel.setOriginalFormData(emptyUser)
	}, [locationState?.user, id, navigate, viewModel])

	useUpdateEffect(() => {
		const selectedUser = toJS(viewModel.selectedUser)
		viewModel.setFormData({ ...viewModel.formData, ...selectedUser })
		viewModel.setOriginalFormData({ ...viewModel.formData, ...selectedUser })
		viewModel.setEditMode(selectedUser.id !== undefined)
	}, [viewModel.selectedUser])
	const formData = toJS(viewModel.formData)
	const originalFormData = useMemo(() => toJS(viewModel.originalFormData), [viewModel.originalFormData])
	const formModified = JSON.stringify(formData) !== JSON.stringify(originalFormData)

	const onSubmitForm = async () => {
		await formRef.current?.customValidateFields(() => {
			if (!formData.role) {
				throw { message: t("validations.role") }
			}
			if (!formData.status) {
				throw { message: t("validations.status") }
			}
			if (!formData.birthdate) {
				throw { message: t("validations.birthdate") }
			}
		})
		await viewModel.sendFormData()
		const createdOrExistingUserId = viewModel.formData.id || toJS(viewModel.selectedUser).id
		navigate(id ? "." : `/users/${createdOrExistingUserId}/edit`, {
			replace: true,
			state: {
				...locationState,
				user: {
					...toJS(viewModel.formData)
				}
			}
		})
	}
	return (
		<div>
			<MainForm
				onSubmitForm={onSubmitForm}
				initialValues={{
					...formData,
					birthdate: formData.birthdate ? User.sanitizeDate(formData.birthdate) : formData.birthdate
				}}
				formModified={formModified}
				ref={formRef}
			>
				<Row gutter={[24, 0]}>
					<Col xl={24} md={24} xs={24}>
						<Card
							bordered={false}
							className="criclebox tablespace mb-24"
							title={
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<h6 className="font-semibold m-0">{t("userInfo.cardTitle")}</h6>
								</div>
							}
							bodyStyle={{ padding: "24px" }}
						>
							<UserInformationFormSection
								userLanguage={formData?.language as Language}
								picture={formData?.picture}
								role={formData?.role}
								status={formData?.status}
								inputDisabled={!!formData?.id}
								sealKey={formData?.endorserTypeKey}
								originSite={formData?.microsite ?? formData?.country}
								onChange={value => {
									viewModel.setFormData({ ...formData, ...value })
								}}
							/>
						</Card>
					</Col>
				</Row>
			</MainForm>
		</div>
	)
}

export default observer(UsersFormView)
